// Address API client for the marketplace customer embed.
//
// Wire shape mirrors the Go model exactly (project rule §39):
//   model: internal/marketplace/model/address.go
//   handler: internal/marketplace/handler/customer_handler.go
//     - HandleAddressList (GET /api/v1/marketplace/me/addresses)
//     - HandleAddressCreate (POST /api/v1/marketplace/me/addresses)
//     - HandleAddressUpdate (PATCH /api/v1/marketplace/me/addresses/{id})
//     - HandleAddressDelete (DELETE /api/v1/marketplace/me/addresses/{id})
//
// All fields use snake_case matching the Go `json` tags.
// `line2` is nullable (*string in Go, omitempty).
// `id` is int64 in Go (number in TS — safe for JS up to 2^53).
//
// NOTE on is_default (T5, verified against internal/marketplace/handler/
// customer_handler.go + internal/marketplace/repo/address_pgx.go 2026-08-16):
// the field genuinely exists on the wire and is genuinely PATCHable by the
// customer. BUT unlike the admin surface (admin_handler_customer_admin.go,
// which wraps Create/Update in a transaction with the companion
// UnsetDefaultsForTypeInTx call to demote the customer's other addresses of
// the same type), the customer-facing HandleAddressUpdate calls the plain
// (non-Tx) Address.Update — it does NOT call UnsetDefaultsForTypeInTx, and
// there is no DB-level unique constraint enforcing "one default per type"
// either. Setting is_default:true via this client can therefore leave TWO
// addresses of the same type marked default at the DB layer. updateAddress()
// below reflects the wire faithfully; AccountCenter.tsx applies an optimistic
// same-session local demotion for UX only — it does not and cannot fix the
// server-side invariant. Flagged for backend follow-up (out of scope here —
// T5 is embed-frontend only, no backend/migration changes authorized).

import { embedApiFetch } from "../../embed-shared/api-client";

/**
 * Address as returned by the Go API. Mirrors model.Address exactly.
 * Go json tags: id, customer_id, address_type, is_default, line1,
 * line2 (omitempty), city, state, postal_code, country_code, version.
 */
export interface AddressWire {
  id: number;
  customer_id: number;
  address_type: string;  // "shipping" | "billing"
  is_default: boolean;
  line1: string;
  line2?: string;        // nullable, omitted when null (Go omitempty)
  city: string;
  state: string;
  postal_code: string;
  country_code: string;  // 2-char ISO, default "US" server-side
  version: number;
}

/**
 * Request body for POST /api/v1/marketplace/me/addresses.
 * Mirrors the handler's `body` struct. customer_id is sourced from JWT
 * (never sent by client — security: prevents cross-customer write).
 */
export interface CreateAddressRequest {
  address_type: string;
  is_default: boolean;
  line1: string;
  line2: string | null;
  city: string;
  state: string;
  postal_code: string;
  country_code: string;
}

/**
 * List all addresses for the authenticated customer.
 *
 * GET /api/v1/marketplace/me/addresses
 * Response envelope (after embedApiFetch .data unwrap):
 *   { addresses: AddressWire[] }
 */
export async function listAddresses(): Promise<AddressWire[]> {
  const result = await embedApiFetch<{ addresses: AddressWire[] }>(
    "/api/v1/marketplace/me/addresses",
  );
  return result?.addresses ?? [];
}

/**
 * Create a new address for the authenticated customer.
 *
 * POST /api/v1/marketplace/me/addresses
 * Response (after embedApiFetch .data unwrap): AddressWire (with id, version=1).
 */
export async function createAddress(req: CreateAddressRequest): Promise<AddressWire> {
  const result = await embedApiFetch<AddressWire>(
    "/api/v1/marketplace/me/addresses",
    { method: "POST", body: req },
  );
  return result;
}

/**
 * Partial-update request body for PATCH /api/v1/marketplace/me/addresses/{id}.
 * Mirrors the handler's `body` struct exactly — every field but `version` is
 * optional (omitted fields are left unchanged server-side via SQL COALESCE).
 * `version` is REQUIRED (optimistic locking; 409 VERSION_MISMATCH on stale).
 */
export interface UpdateAddressRequest {
  version: number;
  address_type?: string;
  is_default?: boolean;
  line1?: string;
  line2?: string | null;
  city?: string;
  state?: string;
  postal_code?: string;
  country_code?: string;
}

/**
 * Update an existing address for the authenticated customer.
 *
 * PATCH /api/v1/marketplace/me/addresses/{id}
 * Response (after embedApiFetch .data unwrap): AddressWire with version+1.
 * Throws ApiError(404) if the address doesn't belong to this customer,
 * ApiError(409, "VERSION_MISMATCH") on a stale version.
 */
export async function updateAddress(
  id: number,
  req: UpdateAddressRequest,
): Promise<AddressWire> {
  return embedApiFetch<AddressWire>(
    `/api/v1/marketplace/me/addresses/${id}`,
    { method: "PATCH", body: req },
  );
}

/**
 * Soft-delete an address for the authenticated customer.
 *
 * DELETE /api/v1/marketplace/me/addresses/{id}
 * Version is passed in the body (consistent with the PATCH pattern on this
 * surface — NOT an If-Match header). 204 No Content on success; embedApiFetch
 * resolves 204 to `undefined`. Throws ApiError(404) or ApiError(409,
 * "VERSION_MISMATCH") the same way updateAddress does.
 */
export async function deleteAddress(id: number, version: number): Promise<void> {
  await embedApiFetch<void>(
    `/api/v1/marketplace/me/addresses/${id}`,
    { method: "DELETE", body: { version } },
  );
}
